import { palettes, tokens } from '@treams/treams-ui';

export type TalentReviewCellColors = {
	/** Cell border. */
	border: string;
	/** Label text: the dark shade of the border's hue, so light borders keep readable text. */
	label: string;
	/** Faint tint of the same hue, used for the decorative swirl. */
	soft: string;
};

type Shade = keyof typeof palettes.vividPink;

/**
 * Bands of a single hue, one per border shade. The label takes a dark shade of
 * that same hue so a light border still carries readable text, and the swirl
 * takes its faintest tint.
 * @param palette the hue all three colours come from
 * @param labelShade dark shade used for the label text
 * @param borderShades border shades, light to dark; one band each
 */
function hueBands(
	palette: Record<Shade, string>,
	labelShade: Shade,
	borderShades: Shade[],
): TalentReviewCellColors[] {
	return borderShades.map((shade) => ({
		border: palette[shade],
		label: palette[labelShade],
		soft: palette['50'],
	}));
}

/** Neutral colours for a past day: the grid is history, not something to work in. */
export const pastCellColors: TalentReviewCellColors = {
	border: tokens.border.default,
	label: tokens.text.secondary,
	soft: tokens.surface.secondary,
};

// Colours run along the anti-diagonals, from low/low (bottom left) to high/high (top
// right): peach, then blue, then pink. Several shades per hue, so a 5×5 grid — the
// largest a template allows — still gives every diagonal its own colour. A nine-box
// samples every other band and so keeps the five colours it always had.
const BANDS = [
	...hueBands(palettes.vibrantPeach, '700', ['400', '600']),
	...hueBands(palettes.electricBlue, '600', ['400', '500', '600']),
	...hueBands(palettes.vividPink, '600', ['300', '400', '500', '600']),
];

// Colours for the cell at (row, column) in a grid of rows × columns. Cell colours are
// fixed by position; the colour stored on the template cell is not used for drawing.
export function cellColors(
	row: number,
	column: number,
	rows: number,
	columns: number,
): TalentReviewCellColors {
	const max = rows + columns - 2;
	const band =
		max <= 0 ? BANDS.length - 1 : Math.round(((row + column) / max) * (BANDS.length - 1));

	return BANDS[Math.min(BANDS.length - 1, Math.max(0, band))];
}

// Numeric form of the positional border colour, for the template's required `color` field.
export function cellColorValue(row: number, column: number, rows: number, columns: number) {
	return parseInt(cellColors(row, column, rows, columns).border.slice(1), 16);
}
